import { createRoot, hydrateRoot } from 'react-dom/client';

// "The Ledger" typography foundation (Phase 2.1). Variable fonts loaded once
// here so every route inherits them via Tailwind's fontFamily tokens.
// Source Serif 4 for display, Geist for body, JetBrains Mono for numerals.
// Fraunces was evaluated but its default lowercase "f" carries a descender
// (the "hooked f" quirk) that reads as inconsistent next to conventional
// letterforms at display sizes.
import '@fontsource-variable/source-serif-4';
import '@fontsource-variable/geist';
import '@fontsource-variable/jetbrains-mono';

import App from './App.tsx';
import ErrorBoundary from './components/ErrorBoundary.tsx';
import { installConsentDefaults } from './lib/analytics/consent';
import { preloadConsumerBoard } from './lib/consumerBoardRequest';
import { installVitePreloadRecovery } from './utils/vitePreloadRecovery';
import './index.css';

// Google Consent Mode v2: default-DENIED for all four signals, pushed before
// anything renders. Trusted region and the current notice resolve analytics
// permission; saved opt-outs and Global Privacy Control win. Browser advertising
// stays denied. GTM loads only after this resolves (see ConsentBanner).
installConsentDefaults();

// A visitor can receive an older HTML shell while a production deploy removes
// that shell's hashed lazy-route chunk. Install Vite's documented recovery
// event before React starts hydration so one reload can obtain a matching
// document without first replacing useful prerendered content.
installVitePreloadRecovery();

// Read the existing public board while the lazy rate-page code downloads.
// Cancellation and private design previews must never initiate this request.
if (
  (import.meta.env.VITE_RATE_EXPERIENCE_PUBLIC === 'true'
    || import.meta.env.VITE_RATE_EXPERIENCE_LIVE_PROVIDER === 'true')
  && window.location.pathname.replace(/\/$/, '') === '/rate-experience'
) {
  const query = new URLSearchParams(window.location.search);
  if (query.get('view') !== 'unsubscribe'
    && query.get('view') !== 'manage'
    && query.get('entry') !== 'dscr'
    && query.get('entry') !== 'watch') preloadConsumerBoard();
}

// Read the homepage's officer roster while its route code downloads. It is the
// same module, cache and in-flight request the hero's hook uses; the chunk is
// already part of the homepage graph, so this adds no new download.
if (window.location.pathname === '/') {
  void import('./hooks/useLoanOfficers')
    .then(({ primeLoanOfficers }) => primeLoanOfficers())
    .catch(() => undefined);
}

// Retired public tools persisted financial inputs in localStorage indefinitely.
// Purge only their exact historical keys for every visitor, on every route —
// never a blanket wipe of unrelated storage. Storage can throw (privacy mode,
// disabled cookies); a failed purge must never block the app from rendering.
try {
  for (const retiredKey of [
    'mortgageFormData',
    'enhancedRefinanceInputs',
    'enhancedRefinanceInputs-v2',
  ]) {
    window.localStorage.removeItem(retiredKey);
  }
} catch {
  // Storage unavailable — nothing to purge.
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Failed to find root element. Make sure there is an element with id="root" in your HTML.');
}

const application = (
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);

if (rootElement.dataset.lwPrerendered === 'true') {
  hydrateRoot(rootElement, application, {
    onRecoverableError() {
      // Make a recovered SSR/client mismatch observable to production checks.
      // Do not serialize the message into the DOM: public copy may change, and
      // the boolean signal is all automated monitoring needs.
      document.documentElement.dataset.lwHydrationError = 'true';
    }
  });
} else {
  createRoot(rootElement).render(application);
}
